import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@material-ui/core";
import React, { ChangeEvent, useCallback, useState } from 'react';
import { Accordion, AccordionItem, AccordionItemPanel } from 'react-accessible-accordion';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { Client } from "../../../../api/api";
import { AutocompleteClients } from '../../../../components/autocomplete/AutocompleteClients';
import { useMobile } from "../../../../hooks/system/useMobile";
import { useApi } from "../../../../hooks/useApi";

export interface AddClientDialogProps {
  open: boolean,
  onClose: () => void
}

export interface UserInfoFormType {
  email: string,
  role: "admin" | "client"
}

export const AddClientDialog = (p: AddClientDialogProps) => {
  // State
  const SUPPRESS_NEW_USER_EMAIL: boolean = process.env.REACT_APP_SUPPRESS_NEW_USER_EMAIL !== undefined && process.env.REACT_APP_SUPPRESS_NEW_USER_EMAIL.toLowerCase() === "true",
    isMobile = useMobile('sm'),
    validationSchema = yup.object().shape({
      email: yup.string().email().required(),
      role: yup.string().oneOf(['admin', 'client']).required()
    }),
    { api } = useApi(),
    { register, errors, handleSubmit } = useForm<UserInfoFormType>({validationSchema}),
    [ email, setEmail ] = useState<string>(),
    [ role, setRole ] = useState<'admin' | 'client'>('client'),
    [ client, setClient ] = useState<Client>();

  // Handlers
  const emailHandler = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail((e.target as HTMLInputElement).value);
  }

  const roleHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const role = (e.target as HTMLInputElement).value;
    if (('client' === role) || ('admin' === role)) {
      setRole(role);
    } else {
      console.log("Invalid role selected: ", role);
    }
  }

  const onClose = useCallback(() => {
    p.onClose();
  }, [p]);

  function NoClientAlert() {
    alert("You must specify a client");
  }

  const onSubmit = useCallback(async (data: UserInfoFormType) => {
    setEmail(data.email);
    setRole(data.role);
    if (email && role && client) {
      try {
        const res = await api.postUsers({
          createUserRequest: {
            email: email.toLowerCase(),
            role: role,
            suppressNewUserEmail: SUPPRESS_NEW_USER_EMAIL
          }
        })
        if (res.status === 200 && 'client' === role && client) {
          await api.postUsersAttachClient({
            attachClient: {
              clientId: client.id,
              authInfoId: res.body.id,
              email: email
            }
          })
          onClose();
        }
      } finally {
        onClose();
      }
    } else {
      NoClientAlert()
    }
  }, [api, client, email, role, onClose, SUPPRESS_NEW_USER_EMAIL]);

  return (
    <Dialog open={p.open} onClose={p.onClose} maxWidth='sm' fullWidth fullScreen={isMobile}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>Add User</DialogTitle>
        
        <DialogContent>
          <TextField margin-top='0' fullWidth inputRef={register} label='Email' name='email' error={!!errors?.email?.message} helperText={errors.email?.message} onChange={emailHandler}/>
          <TextField margin='normal' fullWidth defaultValue='' inputRef={register} select SelectProps={{native: true}} label='Role' name='role' error={!!errors?.role?.message} helperText={errors.role?.message} onChange={roleHandler}>
            <option value='client'>Client</option>
            <option value='admin'>Admin</option>
          </TextField>
          <Accordion allowZeroExpanded>
            <AccordionItem dangerouslySetExpanded={'client' === role}>
              <AccordionItemPanel>
                <Box mt={1} />
                <AutocompleteClients value={client ? client : null} onChange={(c) => {setClient(c ? c : undefined)}}/>
                <Box mt={1} />
              </AccordionItemPanel>
            </AccordionItem>
          </Accordion>
        </DialogContent>

        <DialogActions>
          <Button size="small" onClick={p.onClose}>
            <Box m={[0, 1]}>Cancel</Box>
          </Button>
          <Button type='submit' size="small">
            <Box m={[0, 1]}>Submit</Box>
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
